import React from 'react';
import { Card, CardContent, Typography, Box, Skeleton, Tooltip, IconButton } from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';

interface KpiCardProps {
  label: string;
  value: string | number;
  sublabel?: string;
  icon?: React.ReactNode;
  loading?: boolean;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  tooltip?: string | React.ReactNode;
  onClick?: () => void;
  'data-testid'?: string;
}

export const KpiCard: React.FC<KpiCardProps> = ({
  label,
  value,
  sublabel,
  icon,
  loading = false,
  color = 'primary',
  tooltip,
  onClick,
  ...rest
}) => {
  if (loading) {
    return (
      <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }} {...rest}>
        <CardContent>
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="80%" height={40} />
          <Skeleton variant="text" width="40%" />
        </CardContent>
      </Card>
    );
  }

  const cardContent = (
    <Card 
      elevation={0} 
      sx={{ 
        borderRadius: 3, 
        border: '1px solid', 
        borderColor: 'divider', 
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        position: 'relative',
        '&:hover': onClick ? {
          boxShadow: 1,
          transform: 'translateY(-1px)',
          transition: 'all 0.2s ease-in-out'
        } : {}
      }} 
      onClick={onClick}
      {...rest}
    >
      <CardContent>
        {/* Info icon in top-right corner */}
        {tooltip && (
          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
            <Tooltip title={tooltip} arrow placement="top">
              <IconButton 
                size="small" 
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'text.primary'
                  }
                }}
                onClick={(e) => e.stopPropagation()} // Prevent card click when clicking info icon
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        
        <Box display="flex" alignItems="center" gap={2}>
          {icon && (
            <Box sx={{ color: `${color}.main` }}>
              {icon}
            </Box>
          )}
          <Box flex={1}>
            <Typography variant="overline" color="text.secondary" display="block">
              {label}
            </Typography>
            <Typography variant="h4" fontWeight={700} lineHeight={1.2} color={`${color}.main`}>
              {value}
            </Typography>
            {sublabel && (
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                {sublabel}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return cardContent;
};